import { fetchData } from 'Utils/fetch-data';
import type {
  HistoryReportFilterI,
  HistoryReportI,
  HistoryEmployeeReportFilterI,
  HistoryEmployeeReportResponseI,
  HistoryCoursesFilterI,
  HistoryCourseI,
  HistoryCourseEmployeesFilterI,
  HistoryCourseEmployeesResponseI,
} from 'types';

class UserCourseHistoryApi {
  endpoint: string;

  constructor(endpoint: string) {
    this.endpoint = endpoint;
  }

  /**
   * Obtiene todos los empleados disponibles para mostrar su información
   * sobre los cursos que ha tomado.
   * GET /employees
   */
  async getEmployeesCollection(filters: HistoryReportFilterI = {}) {
    const url = `${this.endpoint}/employees`;
    const response = await fetchData<HistoryReportI[]>(url, {
      method: 'GET',
      params: filters,
      includeJsonApiMeta: true,
    });
    return response;
  }

  /**
   * Obtiene la información sobre el historial de los cursos que ha tomado
   * un empleado.
   * GET /employees/:id
   *
   * @param id - ID del empleado (UUID)
   * @param filters - Filtros opcionales (name, assignedAt, expiredAt, page, limit)
   */
  async getEmployeeResource(id: string, filters: HistoryEmployeeReportFilterI = {}) {
    const url = `${this.endpoint}/employees/${id}`;
    const response = await fetchData<HistoryEmployeeReportResponseI>(url, {
      method: 'GET',
      params: filters,
      includeJsonApiMeta: true,
    });
    return response;
  }

  /**
   * Exporta los datos del historial de empleados en un archivo Excel.
   * GET /employees/export
   */
  async exportEmployeesCollection(filters: HistoryReportFilterI = {}) {
    const url = `${this.endpoint}/employees/export`;
    const response = await fetchData<Blob>(url, {
      method: 'GET',
      params: filters,
      responseType: 'blob',
    });
    return response;
  }

  /**
   * Exporta los datos del historial de cursos de un empleado en un archivo Excel.
   * GET /employees/:id/export
   *
   * @param id - ID del empleado (UUID)
   * @param filters - Filtros opcionales para la exportación
   */
  async exportEmployeeResource(id: string, filters: HistoryEmployeeReportFilterI = {}) {
    const url = `${this.endpoint}/employees/${id}/export`;
    const response = await fetchData<Blob>(url, {
      method: 'GET',
      params: filters,
      responseType: 'blob',
    });
    return response;
  }

  /**
   * Obtiene todos los cursos disponibles con estadísticas de capacitación.
   * GET /courses
   *
   * @param filters - Filtros opcionales (name, priorityId, categoryId, page, limit)
   */
  async getCoursesCollection(filters: HistoryCoursesFilterI = {}) {
    const url = `${this.endpoint}/courses`;
    const response = await fetchData<HistoryCourseI[]>(url, {
      method: 'GET',
      params: filters,
      includeJsonApiMeta: true,
    });
    return response;
  }

  /**
   * Obtiene los empleados asignados a un curso específico con filtros.
   * GET /courses/:id
   *
   * @param id - ID del curso (UUID)
   * @param filters - Filtros opcionales (search, startedAtStart, startedAtEnd,
   *                  expiredAtStart, expiredAtEnd, terminalId, departmentId,
   *                  jobTitleId, page, limit)
   */
  async getCourseResource(id: string, filters: HistoryCourseEmployeesFilterI = {}) {
    const url = `${this.endpoint}/courses/${id}`;
    const response = await fetchData<HistoryCourseEmployeesResponseI>(url, {
      method: 'GET',
      params: filters,
      includeJsonApiMeta: true,
    });
    return response;
  }

  /**
   * Exporta los datos de la colección de cursos en un archivo Excel.
   * GET /courses/export
   *
   * @param filters - Filtros opcionales para la exportación
   */
  async exportCoursesCollection(filters: HistoryCoursesFilterI = {}) {
    const url = `${this.endpoint}/courses/export`;
    const response = await fetchData<Blob>(url, {
      method: 'GET',
      params: filters,
      responseType: 'blob',
    });
    return response;
  }

  /**
   * Exporta los datos de los empleados de un curso en un archivo Excel.
   * GET /courses/:id/export
   *
   * @param id - ID del curso (UUID)
   * @param filters - Filtros opcionales para la exportación
   */
  async exportCourseResource(id: string, filters: HistoryCourseEmployeesFilterI = {}) {
    const url = `${this.endpoint}/courses/${id}/export`;
    const response = await fetchData<Blob>(url, {
      method: 'GET',
      params: filters,
      responseType: 'blob',
    });
    return response;
  }

  /**
   * Descarga el certificado de un curso completado.
   * GET /:historyId/certificate/:certificationId/download
   *
   * @param historyId - ID del registro de historial
   * @param certificationId - ID de la certificación
   * @returns Blob del certificado
   */
  async downloadCertificate(historyId: string, certificationId: string) {
    const url = `${this.endpoint}/${historyId}/certificate/${certificationId}/download`;
    const response = await fetchData<Blob>(url, {
      method: 'GET',
      responseType: 'blob',
    });
    return response;
  }
}

const userCourseHistoryApi = new UserCourseHistoryApi('/user-course-history');

export default userCourseHistoryApi;
